
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BaseForm } from "./base-form";
import { PasswordLoginForm } from "./password-login-form";
import { MagicLinkForm } from "./magic-link-form";
import { PasswordResetForm } from "./password-reset-form";
import { SignupForm } from "./signup-form";
import { GoogleSignInButton } from "./google-sign-in-button";

interface LoginFormProps {
  onSuccess: () => void;
  context?: "parent" | "coach";
}

// Properly define component as a function component
export function LoginForm({ onSuccess, context }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  // Handle switching to signup mode
  const handleCreateAccount = () => {
    setShowSignup(true);
  };

  // Handle going back to login
  const handleBackToLogin = () => {
    setShowSignup(false);
  };

  // Show password reset form
  if (resetMode) {
    return (
      <BaseForm title="Reset Password">
        <PasswordResetForm
          email={email}
          setEmail={setEmail}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onBackToLogin={() => setResetMode(false)}
        />
      </BaseForm>
    );
  }

  // Show signup form
  if (showSignup) {
    return (
      <BaseForm title="Create Account">
        <SignupForm
          email={email}
          password={password}
          setEmail={setEmail}
          setPassword={setPassword}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onBackToLogin={handleBackToLogin}
        />
      </BaseForm>
    );
  }

  // Show login form
  return (
    <BaseForm title="Sign In">
      <GoogleSignInButton prominent />

      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">
            Or sign in with email
          </span>
        </div>
      </div>

      <Tabs defaultValue="password" className="w-full">
        <TabsList className="grid grid-cols-2 mb-6">
          <TabsTrigger value="password">Email & Password</TabsTrigger>
          <TabsTrigger value="magic">Magic Link</TabsTrigger>
        </TabsList>
        
        <TabsContent value="password">
          <PasswordLoginForm
            email={email}
            password={password}
            setEmail={setEmail}
            setPassword={setPassword}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onSuccess={onSuccess}
            onResetPassword={() => setResetMode(true)}
            onCreateAccount={handleCreateAccount}
            hideGoogleButton
          />
        </TabsContent>
        
        <TabsContent value="magic">
          <MagicLinkForm
            email={email}
            setEmail={setEmail}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onSuccess={onSuccess}
          />
        </TabsContent>
      </Tabs>
    </BaseForm>
  );
}
